import React, { useState, useEffect } from 'react';
import { getFunnelData, exportFunnelCSV, exportFunnelPDF, FunnelData } from '../lib/funnelApi';

export const ConversionFunnelPage: React.FC = () => {
  // Filters
  const [platformFilter, setPlatformFilter] = useState<'all' | 'meta' | 'google' | 'tiktok'>('all');
  const [dateRange, setDateRange] = useState('7d');
  const [ageRange, setAgeRange] = useState('');
  const [gender, setGender] = useState('');
  const [location, setLocation] = useState('');
  
  // API data
  const [funnelData, setFunnelData] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate dates based on range
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case '24h':
        start.setDate(start.getDate() - 1);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }
    
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  // Fetch funnel data
  useEffect(() => {
    const fetchFunnel = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { start_date, end_date } = getDateRange();
        const data = await getFunnelData({
          start_date,
          end_date,
          platform: platformFilter,
          age_range: ageRange || undefined,
          gender: gender || undefined,
          location_country: location || undefined
        });
        setFunnelData(data);
      } catch (err) {
        console.error('Failed to load funnel:', err);
        setError('Failed to load funnel data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFunnel();
  }, [platformFilter, dateRange, ageRange, gender, location]);

  // Export handlers
  const handleExport = (format: 'csv' | 'pdf') => {
    const { start_date, end_date } = getDateRange();
    const params = {
      start_date,
      end_date,
      platform: platformFilter,
      age_range: ageRange || undefined,
      gender: gender || undefined,
      location_country: location || undefined
    };
    
    if (format === 'csv') {
      exportFunnelCSV(params);
    } else {
      exportFunnelPDF(params);
    }
  };

  const getStageWidth = (count: number, maxCount: number) => {
    return Math.max((count / maxCount) * 100, 10); // Min 10% width for visibility
  };

  const maxUsers = funnelData?.stages[0]?.count || 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Conversion Funnel</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Visualize user journey from impression to purchase
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Platforms</option>
            <option value="meta">Meta</option>
            <option value="google">Google</option>
            <option value="tiktok">TikTok</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* NEW: Demographic Filters & Export Buttons */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-3">
            <select
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Ages</option>
              <option value="18-24">18-24</option>
              <option value="25-34">25-34</option>
              <option value="35-44">35-44</option>
              <option value="45-54">45-54</option>
              <option value="55+">55+</option>
            </select>

            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>

            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Locations</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              disabled={loading}
            >
              📥 Export CSV
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              disabled={loading}
            >
              📄 Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gp-sungold"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Stats Overview */}
      {!loading && funnelData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Impressions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {funnelData.stages[0]?.count.toLocaleString() || '0'}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Purchases</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {funnelData.stages[funnelData.stages.length - 1]?.count.toLocaleString() || '0'}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Overall Conversion</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {funnelData.overall_conversion}%
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Order Value</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                ${funnelData.average_order_value.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Funnel Visualization */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Funnel Stages</h2>
            <div className="space-y-6">
              {funnelData.stages.map((stage, index) => (
                <div key={stage.name}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gp-sungold text-white text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">{stage.name}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <p className="text-gray-600 dark:text-gray-400">Users</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{stage.count.toLocaleString()}</p>
                      </div>
                      {index > 0 && (
                        <div className="text-right">
                          <p className="text-gray-600 dark:text-gray-400">Drop-off</p>
                          <p className="font-semibold text-red-600">{stage.drop_off}%</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-gray-600 dark:text-gray-400">Conv. Rate</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{stage.rate}%</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative h-16 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-gp-sungold to-gp-sungold-600 flex items-center justify-center text-white font-medium transition-all duration-300"
                      style={{ width: `${getStageWidth(stage.count, maxUsers)}%` }}
                    >
                      {stage.count.toLocaleString()} users
                    </div>
                  </div>
                  {index < funnelData.stages.length - 1 && (
                    <div className="flex justify-center my-2">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
            <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4">
              💰 Revenue Insights
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Total Revenue</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                  ${funnelData.total_revenue.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Average Order Value</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                  ${funnelData.average_order_value.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Conversion Rate</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                  {funnelData.overall_conversion}%
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConversionFunnelPage;
